export { Storage } from './storage';
import { getSentryScope } from '@/services/sentry';

export const CC_GATEWAYS = {
  STRIPE: 'stripe',
  CARDKNOX: 'cardknox',
  GROW: 'grow'
};

export const GATEWAYS = {
  ZELLE: 'zelle',
  STRIPE: 'stripe',
  CARDKNOX: 'cardknox',
  GROW: 'grow',
  CHARIOT: 'chariot',
  PAYPAL: 'paypal'
};

export const PAYMENT_METHODS = {
  ZELLE: 'zelle',
  CARD: 'card',
  GPAY: 'g-pay',
  APPLE_PAY: 'apple-pay',
  ACH: 'ach',
  DAF: 'daf',
  BANK_WALLET: 'bank-wallet'
};

export const widgetViews = {
  DONATION_FORM: {
    name: 'donation-form',
    level: 1
  },
  UPSELL: {
    name: 'upsell',
    level: 2
  },
  ADDITIONAL_DETAILS: {
    name: 'additional-details',
    level: 3
  },
  CREDIT_CARD_FORM: {
    name: 'credit-card-form',
    level: 4
  },
  BANK_FORM: {
    name: 'bank-form',
    level: 4
  },
  CONFIRMATION_DONATION: {
    name: 'confirmation-donation',
    level: 5
  },
  CONFIRMATION_FUNDRAISER: {
    name: 'confirmation-fundraiser',
    level: 5
  },
  FUNDRAISER_SIGNUP: {
    name: 'fundraiser-signup',
    level: 3
  },
  DONOR_WALL: {
    name: 'donor-wall',
    level: 3
  }
};

export const MAX_AMOUNT = 1000000;
export const MIN_AMOUNT = 5;

export { sanitizeEmail } from './sanitize-email';

export function getCurrencySymbol(currency) {
  if (!currency) {
    return;
  }

  const currencySymbols = {
    usd: '$',
    eur: '€',
    ils: '₪',
    gbp: '£',
    cad: 'C$',
    aud: 'A$'
  };

  const symbol = currencySymbols[currency.toLowerCase()] || '';
  if (!symbol) {
    throw new Error('Unknown currency');
  }

  return symbol;
}

export function loadScript(src, { target, type, isAsync, nomodule, attributes }, callback) {
  return new Promise((resolve, reject) => {
    const element = document.createElement('script');
    element.src = src;

    if (type) {
      element.type = type;
    }
    if (nomodule) {
      // For boolean attributes, we need to use setAttribute
      element.setAttribute('nomodule', '');
    }

    if (isAsync) {
      element.async = true;
    }

    // Add custom attributes
    if (attributes) {
      Object.keys(attributes).forEach((key) => {
        element.setAttribute(key, attributes[key]);
      });
    }

    if (callback) {
      element.addEventListener('load', callback);
    }

    element.addEventListener('error', reject);

    element.addEventListener('load', () => {
      resolve();
    });

    target.appendChild(element);
  });
}

export function loadStyleLink(href, { target, rel }) {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = rel || 'stylesheet';
    link.href = href;
    target.appendChild(link);

    link.addEventListener('load', () => {
      resolve();
    });

    link.addEventListener('error', reject);
  });
}

export function unparam(param) {
  // Remove the leading ? if present
  const queryString = param.split('?').slice(1).join('?').split('?')[0] || param;
  const queryParams = new URLSearchParams(queryString);
  const params = {};

  for (const [key, value] of queryParams) {
    params[key] = value;
  }

  return params;
}

const donationStatuses = {
  initial: 'initial', // pending payment method validation
  paymentMethodError: 'paymentMethodError',
  pending: 'pending', // payment was submitted and is awaiting final settlement
  paymentMethodSuccess: 'paymentMethodSuccess', // still pending 1st charge
  subscriptionError: 'subscriptionError', // error creating a subscription
  subscriptionScheduled: 'subscriptionScheduled', // subscription scheduled for future start
  subscriptionSuccess: 'subscriptionSuccess', // subscription created successfully
  firstChargeError: '1stChargeError', // 1st charge failed
  firstChargeSuccess: '1stChargeSuccess', // 1st charge succeeded; more charges still pending
  inProgressError: 'inProgressError', // a charge failed; more charges still pending
  endedError: 'endedError', // ended; some charges failed
  endedSuccess: 'endedSuccess' // All charges succeeded
};

export function donationStatusIsAtLeast(source, dest) {
  const statuses = Object.values(donationStatuses);
  if (!statuses.includes(dest)) {
    throw new Error(`Invalid status: ${dest}`);
  }
  return statuses.indexOf(source) >= statuses.indexOf(dest);
}

export function handleNumberInput(event, maxInputLength = 7) {
  const which = event.which;
  const allowedKeys = {
    backspace: 8,
    tab: 9
  };

  if (Object.values(allowedKeys).includes(which)) {
    return;
  }

  const isNumberKey = (which >= 48 && which <= 57) || (which >= 96 && which <= 105);
  if (!isNumberKey) {
    event.preventDefault();
  }

  if (event.target.value.length >= maxInputLength) {
    event.preventDefault();
  }
}

export function splitName(name) {
  name = name.trim();
  const lastSpace = name.lastIndexOf(' ');
  let firstName = name.substring(0, lastSpace);
  const lastName = name.substring(lastSpace + 1);
  if (!firstName) {
    // i.e. no space in name
    firstName = lastName;
  }

  return {
    firstName,
    lastName
  };
}

export function setCampaignInUrl(campaignSlug, isOpen = true) {
  const url = new URL(window.location.href);
  if (isOpen) {
    url.searchParams.set('campaign', campaignSlug);
  } else {
    url.searchParams.delete('campaign');
  }
  window.history.pushState({}, '', url);
}

export function removeCampaignFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('campaign');
  window.history.pushState({}, '', url);
}

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function evaluateAmountCondition(condition, amount) {
  if (!condition || amount === null || amount === undefined || amount === '') return false;
  const conditionText = String(condition).trim();
  const numAmount = Number(amount);
  if (!conditionText || !Number.isFinite(numAmount)) return false;
  if (conditionText.includes(',')) {
    const exactAmounts = conditionText.split(',').map((value) => value.trim());
    if (exactAmounts.some((value) => !value || !Number.isFinite(Number(value)))) return false;
    return exactAmounts.some((value) => numAmount === Number(value));
  }
  if (conditionText.includes('-')) {
    const [start, end] = conditionText.split('-').map(Number);
    return numAmount >= start && numAmount <= end;
  }
  if (conditionText.startsWith('>=')) return numAmount >= Number(conditionText.substring(2));
  if (conditionText.startsWith('<=')) return numAmount <= Number(conditionText.substring(2));
  if (conditionText.startsWith('>')) return numAmount > Number(conditionText.substring(1));
  if (conditionText.startsWith('<')) return numAmount < Number(conditionText.substring(1));
  return numAmount === Number(conditionText);
}

export function checkShouldSkipAdditionalDetails(settings, donation) {
  const isCommentHidden = !!settings.hideComment;
  const isAnonymousHidden = !!settings.hideAnonymously;
  const isDedicationHidden = !!settings.hideDedication;

  const hasVisibleCustomQuestions = (() => {
    if (!settings.isCustomQuestionsActive || !settings.customQuestions?.length) return false;
    const { amount, amountDescription } = donation;
    return settings.customQuestions.some(({ ifAmount, ifAmountDescription }) => {
      if (!ifAmount && !ifAmountDescription) return true;
      const amountMatch = ifAmount ? evaluateAmountCondition(ifAmount, amount) : true;
      const descriptionMatch = ifAmountDescription ? ifAmountDescription === amountDescription : true;
      return amountMatch && descriptionMatch;
    });
  })();

  return isCommentHidden && isAnonymousHidden && isDedicationHidden && !!settings.hideCompanyDonation && !hasVisibleCustomQuestions;
}

export function sentryMessage(message, level, context) {
  try {
    if (context) {
      const contextString = JSON.stringify(context.data);
      const contextObj = JSON.parse(contextString);
      getSentryScope().setContext(context.name, contextObj);
    }

    getSentryScope().captureMessage(message, level);
  } catch (error) {
    console.log('Error in sentryWarning: ', error);
  }
}
